import { createSelector } from 'reselect';
import { makeGetActiveAreas, getAllAreas } from '../dsa/areaSelectors';
import {uniq, sortBy, compact} from 'lodash';
import { getGoalAnswers } from '../dsa/goalsSelectors';
import { getAnalysisValueForGoalAndGroup } from '../../utils/analysisUtils';
import { getAbilities } from '../dsa/abilitiesSelectors';

// analysis goals filtered by areaId for current area
const getAllAnalysisGoals = (state) => {
  return state.dsa && state.dsaConfig.analysisGoals;
};

// analysis goals filtered by areaId for current area
const getAnalysisGoals = (state) => {
  return state.analysisGoals || [];
};

const getAreas = (state) => {
  return state.areas || [];
};

const getDSAGoals = (state) => {
  return state.dsa && state.dsa.dsaGoals;
};

// analysis goals filtered by areaId for current area
const getAnalysisGoalsForArea = (state, areaId) => {
  return state.dsa &&
    state.dsaConfig.analysisGoals &&
    state.dsaConfig.analysisGoals
      .filter(goal => goal.area === parseInt(areaId));
};

export const getUserGroups = (state) => {
  return state.dsa && state.dsa.userGroups;
};

export const getOrderedUserGroups = createSelector(
  [getUserGroups],
  (userGroups) => {
    return userGroups.concat().sort(group => -group.order);
  }
);

const getAnalysisUserGroups = state => {
  return state.dsa && state.dsa.analysisUserGroups || [];
};

function hasGroups(groups, goalId, values, type) {
  return groups.map((group) => {
    return getAnalysisValueForGoalAndGroup(goalId, group.id, values);
  })
    .filter(value => value === type);
}

function getCount(groups, goals, values, type) {
  const filtered = goals.filter((goal) => {
    return hasGroups(groups, goal.id, values, type).length > 0;
  });
  return filtered.length;
}

const getAdoptionGoalsForArea = (state, areaId) => {
  const analysisGoals = getAnalysisGoalsForArea(state, areaId);
  const userGroups = getUserGroups(state);
  const analysisUserGroups = getAnalysisUserGroups(state);
  const areas = getAllAreas(state);

  // ID's for lookup
  const analysis_goal_ids = analysisGoals.map(g => g.id);

  // array of unique user group ID's linked to the analysis goals linked to this area
  const user_group_ids = uniq(analysisUserGroups.filter(aug => analysis_goal_ids.indexOf(aug.analysisgoal) !== -1).map(aug => aug.usergroup));

  // get user groups for these IDs and sort
  let user_groups = user_group_ids.map(id => userGroups.find(ug => ug.id === id));
  user_groups = sortBy( user_groups, 'order');

  return {
    area: areas.find(area => area.id === areaId),
    goals: analysisGoals,
    groups: user_groups,
    values: analysisUserGroups
  };
};

export const getAdoptionGoalsForCurrentArea = (state, props) => {
  return getAdoptionGoalsForArea(state, props.params.areaId);
};

// The purpose of this selector is to package up the recommendations goals to be viewed on one page for pptx
export const getAdoptionGoalsWithAreas = type => createSelector(
  [getAnalysisGoals, getUserGroups, getAnalysisUserGroups, getAreas, getDSAGoals],
  (analysisGoals, userGroups, analysisUserGroups, areas, dsaGoals) => {

    const areasWithGoals = areas.map(area => {
      const allAreaGoals = analysisGoals.filter(goal => {
        return area.id === goal.area;
      });

      // ID's for lookup
      const analysis_goal_ids = allAreaGoals.map(g => g.id);

      // array of unique user group ID's linked to the analysis goals linked to this area
      const user_group_ids = uniq(analysisUserGroups.filter(aug => analysis_goal_ids.indexOf(aug.analysisgoal) !== -1).map(aug => aug.usergroup));

      // get user groups for these IDs and sort
      let groups = user_group_ids.map(id => userGroups.find(ug => ug.id === id));
      groups = sortBy( groups, 'order');

      const goals = allAreaGoals.map(goal => {
        const isKeyGoal = dsaGoals.find(ag => ag.goal === goal.goal).answer > 0;
        const goalGroups = groups.filter(group => getAnalysisValueForGoalAndGroup(goal.id, group.id, analysisUserGroups) === type);

        return { ...goal, isKeyGoal, groups: goalGroups };
      });

      return { area, goals, groups, values: analysisUserGroups, type };
    });

    return { areasWithGoals };
  }
);


export const makeGetAdoptionGoalTotals = () => createSelector(
  [getAllAnalysisGoals, getUserGroups, getAnalysisUserGroups, getDSAGoals],
  (analysisGoals, userGroups, analysisUserGroups, keyGoals) => {

    // ID's for lookup
    const analysis_goal_ids = analysisGoals.map(g => g.id);
    const user_group_ids = uniq(analysisUserGroups.filter(aug => analysis_goal_ids.indexOf(aug.analysisgoal) !== -1).map(aug => aug.usergroup));

    let user_groups = user_group_ids.map(id => userGroups.find(ug => ug.id === id));
    user_groups = sortBy( user_groups, 'order');

    const dsaKeyGoals = analysisGoals.filter((goal) => {
      return keyGoals.find(ag => ag.goal === goal.id).answer > 0;
    });

    return {
      moreData: getCount(user_groups, dsaKeyGoals, analysisUserGroups, 0),
      existingData: getCount(user_groups, dsaKeyGoals, analysisUserGroups, 1)
    };
  }
);

export const getAdoptionGoalsByAreaForActiveAreas = (state) => {
  const getActiveAreas = makeGetActiveAreas();
  const activeAreas = getActiveAreas(state);

  return activeAreas.map(area => getAdoptionGoalsForArea(state, area.id));
};

export const getAdoptionGoalsByAreaForAllAreas = (state) => {
  const areas = getAllAreas(state);

  return areas.map(area => getAdoptionGoalsForArea(state, area.id));
};

export const getAdoptionGoalsByAreaForOverviewChart = (state) => {
  const adoptionGoalsByArea = getAdoptionGoalsByAreaForAllAreas(state);
  const abilities = getAbilities(state);

  // Combine the IT Operations and Application Support areas to reduce the chart size
  const itOperationsArea = adoptionGoalsByArea.find(area => area.area.title === 'IT Operations');
  const applicationSupportArea = adoptionGoalsByArea.find(area => area.area.title === 'Application Support');

  let combinedArea;

  if (itOperationsArea && applicationSupportArea) {
    const applicationSupportGoalMapping = applicationSupportArea.goals.map(goal => {
      const matchingGoal = itOperationsArea.goals.find(g=> goal.title === g.title);
      return [goal.id, matchingGoal.id];
    });

    const applicationSupportGoalIds = applicationSupportArea.goals.map(g => g.id);

    const combinedValues = itOperationsArea.values.map(value => {
      if (applicationSupportGoalIds.includes(value.analysisgoal)) {
        const mapping = applicationSupportGoalMapping.find(m => {
          return value.analysisgoal === m[0];
        });

        return Object.assign({}, value, {
          analysisgoal: mapping[1]
        });
      }

      return value;
    });

    combinedArea = Object.assign({}, itOperationsArea, {
      area: Object.assign({}, itOperationsArea.area, {
        title: 'IT Operations & Application Support'
      }),
      values: combinedValues
    });
  } else if (itOperationsArea) {
    combinedArea = itOperationsArea;
  } else if (applicationSupportArea) {
    combinedArea = applicationSupportArea;
  }

  return compact([combinedArea]).concat(
    adoptionGoalsByArea
      .filter(area => area.area.title !== 'IT Operations')
      .filter(area => area.area.title !== 'Application Support')
      .filter(area => abilities.canActivateArea(area.area))
  );
};


// Orders user groups by area, then by their order within it.
// This is used to populate the adoption overview chart in the PPTX.
export const getUserGroupsForOverviewChart = createSelector(
  [getUserGroups, getAbilities, getAllAreas],
  (groups, abilities, areas) => {
    return groups
      .filter(g => {
        const area = areas.find(a => a.id === g.area);
        return abilities.canActivateArea(area);
      })
      .sort((a, b) => (a.area - b.area) || (a.order - b.order));
  }
);



export const getKeyAdoptionGoalsByArea = createSelector(
  [getAdoptionGoalsByAreaForActiveAreas, getGoalAnswers],
  (adoptionGoalsByArea, dsaGoals) => (
    adoptionGoalsByArea.map(area => (
      Object.assign({}, area, {
        goals: area.goals
          .filter(goal => (
            dsaGoals.find(dsaGoal => dsaGoal.goal === goal.goal).answer > 0
          ))
      })
    ))
  )
);




// WEBPACK FOOTER //
// ./src/selectors/analytics/adoptionSelectors.js